import type { Collection, UtilsRecord } from "@tanstack/db";
import { useEffect } from "react";
import type { FhirCollectionUtils } from "../fhir/base";
import type { FhirCollectionName, FhirCollectionRegistry } from "../fhir/types";
import { useDataStoreClient } from "./provider";

export interface UseCollectionOptions {
  /**
   * Whether this caller actually wants the collection's data. Default true.
   *
   * Registering as a consumer is what starts a collection's subscription and
   * its first full fetch, and that registration is sticky for the session. A
   * hook called with no parameters — a list hook standing in for a lookup, or
   * one rendered by the app shell on every route — would otherwise pull a
   * whole collection the user never asked to see, on every page.
   *
   * The collection itself is still returned either way, so a caller can hold
   * the hook order steady and decide per render.
   */
  enabled?: boolean;
}

/**
 * Type-safe overload: known FHIR collection names resolve to their resource types.
 */
export function useCollection<K extends FhirCollectionName>(
  name: K,
  options?: UseCollectionOptions,
): Collection<FhirCollectionRegistry[K], string, FhirCollectionUtils<FhirCollectionRegistry[K]>>;

/**
 * Generic fallback for non-FHIR or dynamic collections.
 */
export function useCollection<T extends object, K extends string = string, U extends UtilsRecord = UtilsRecord>(
  name: string,
  options?: UseCollectionOptions,
): Collection<T, K, U>;

/**
 * Hook to access a registered collection by name.
 * Throws if collection not found.
 * Registers as a consumer for lazy subscription tracking, unless disabled.
 */
export function useCollection(name: string, options?: UseCollectionOptions) {
  const client = useDataStoreClient();
  const enabled = options?.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;
    return client.registerConsumer(name);
  }, [client, name, enabled]);

  return client.getCollection(name);
}
